import React, { useState, useEffect } from 'react';

const OnboardingTour = () => {
  const [isActive, setIsActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [hasSeenTour, setHasSeenTour] = useState(false);

  const steps = [
    {
      target: 'order-form',
      title: '👋 Добро пожаловать!',
      description: 'Здесь вы можете восстановить VLESS подписку по номеру заказа с Яндекс Маркета. Давайте быстро покажем, как это работает!',
      position: 'bottom'
    },
    {
      target: 'order-input',
      title: '📝 Введите номер заказа',
      description: 'Вставьте номер вашего заказа сюда. Номер можно найти в разделе "Мои заказы" на Яндекс Маркете.',
      position: 'bottom'
    },
    {
      target: 'submit-button',
      title: '🚀 Получите подписку',
      description: 'Нажмите эту кнопку, чтобы восстановить подписку. Процесс занимает всего несколько секунд!',
      position: 'top'
    },
    {
      target: 'history-section',
      title: '📚 История восстановлений',
      description: 'Все ваши подписки сохраняются здесь. Вы можете добавлять заметки, теги и отмечать важные звёздочкой!',
      position: 'top'
    },
    {
      target: 'stats-button',
      title: '📊 Ваша статистика',
      description: 'Здесь можно посмотреть вашу статистику использования, достижения и многое другое!',
      position: 'bottom'
    },
    {
      target: 'help-button',
      title: '❓ Нужна помощь?',
      description: 'Горячие клавиши и справка всегда под рукой. Нажмите Ctrl+/ чтобы открыть список команд!',
      position: 'bottom'
    }
  ];

  useEffect(() => {
    // Проверяем, видел ли пользователь тур ранее
    const tourSeen = localStorage.getItem('onboarding_completed');
    setHasSeenTour(!!tourSeen);
    
    if (!tourSeen) {
      // Запускаем тур через 1 секунду после загрузки
      const timer = setTimeout(() => {
        setIsActive(true);
      }, 1000);
      
      return () => clearTimeout(timer);
    }
  }, []);

  const nextStep = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      completeTour();
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const skipTour = () => {
    completeTour();
  };

  const completeTour = () => {
    localStorage.setItem('onboarding_completed', 'true');
    setIsActive(false);
    setHasSeenTour(true);
  };

  const restartTour = () => {
    setCurrentStep(0);
    setIsActive(true);
  };

  if (!isActive) {
    // Показываем маленькую кнопку для повторного запуска тура
    if (hasSeenTour) {
      return (
        <button
          onClick={restartTour}
          className="fixed bottom-4 left-4 z-40 p-3 bg-primary-600 hover:bg-primary-700 text-white rounded-full shadow-lg transition-all hover:scale-110"
          title="Пройти тур заново"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </button>
      );
    }
    return null;
  }

  const step = steps[currentStep];

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 bg-black bg-opacity-60 z-50 transition-opacity animate-fade-in" />

      {/* Tooltip */}
      <div className="fixed z-50 animate-scale-in" style={{
        bottom: '50%',
        left: '50%',
        transform: 'translate(-50%, 50%)',
        maxWidth: '90%',
        width: '500px'
      }}>
        <div className="bg-white rounded-xl shadow-2xl p-6">
          {/* Progress bar */}
          <div className="mb-4">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium text-gray-600">
                Шаг {currentStep + 1} из {steps.length}
              </span>
              <button
                onClick={skipTour}
                className="text-xs text-gray-500 hover:text-gray-700"
              >
                Пропустить тур
              </button>
            </div>
            <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-primary-500 to-primary-600 transition-all duration-300"
                style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
              />
            </div>
          </div>

          {/* Content */}
          <div className="mb-6">
            <h3 className="text-2xl font-bold text-gray-900 mb-3">
              {step.title}
            </h3>
            <p className="text-gray-600 leading-relaxed">
              {step.description}
            </p>
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between">
            <button
              onClick={prevStep}
              disabled={currentStep === 0}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                currentStep === 0
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-gray-200 hover:bg-gray-300 text-gray-800'
              }`}
            >
              ← Назад
            </button>

            <div className="flex gap-1">
              {steps.map((_, index) => (
                <div
                  key={index}
                  className={`w-2 h-2 rounded-full transition-all ${
                    index === currentStep
                      ? 'bg-primary-600 w-4'
                      : index < currentStep
                      ? 'bg-primary-400'
                      : 'bg-gray-300'
                  }`}
                />
              ))}
            </div>

            <button
              onClick={nextStep}
              className="px-6 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-medium transition-colors"
            >
              {currentStep === steps.length - 1 ? 'Завершить 🎉' : 'Далее →'}
            </button>
          </div>
        </div>

        {/* Arrow pointer */}
        <div className="absolute -bottom-4 left-1/2 transform -translate-x-1/2">
          <div className="w-8 h-8 bg-white transform rotate-45" />
        </div>
      </div>

      {/* Highlight effect - optional, можно добавить подсветку элементов */}
    </>
  );
};

export default OnboardingTour;











